import Popover from '@components/popovers/Popover';
import PopoverItem from '@components/popovers/PopoverItem';
import useDestroyGoal from '@hooks/mutations/useDestroyGoal';
import useMutationWithValidation from '@hooks/useMutationWithValidation';
import useOpener from '@hooks/useOpener';
import {
  GetJourneyWithPlanQuery,
  GoalFragment,
  GoalUpdateDocument,
  GoalUpdateMutation,
  GoalUpdateMutationVariables
} from '@typing/Generated';

type Props = {
  color?: 'shadesWhite';
  disabled?: boolean;
  goal: GoalFragment;
  journey: GetJourneyWithPlanQuery['journey'];
  setIsExploding?: (v: boolean) => void;
};

const GoalPopover = ({ color, disabled = false, goal, journey, setIsExploding }: Props) => {
  const opener = useOpener();

  const [goalToggleCompleted] = useMutationWithValidation<GoalUpdateMutation, GoalUpdateMutationVariables>(
    GoalUpdateDocument,
    'goal',
    {
      update: store => {
        store.modify({
          fields: {
            completed() {
              return !goal.completed;
            }
          },
          id: store.identify({ ...goal })
        });
      }
    }
  );

  const [goalArchived] = useMutationWithValidation<GoalUpdateMutation, GoalUpdateMutationVariables>(
    GoalUpdateDocument,
    'goal',
    {
      update: store => {
        store.modify({
          fields: {
            archived() {
              return !goal.archived;
            }
          },
          id: store.identify({ ...goal })
        });
      }
    }
  );

  const goalDestroy = useDestroyGoal(journey);

  const updateComplete = () => {
    const completed = !goal.completed;

    goalToggleCompleted({
      completed,
      id: goal.id
    }).then(() => {
      opener.close();
      if (completed && setIsExploding) {
        setIsExploding(true);
        setTimeout(() => {
          setIsExploding(false);
        }, 1000);
      }
    });
  };

  const updateArchived = () => {
    goalArchived({
      archived: !goal.archived,
      id: goal.id
    });
    opener.close();
  };

  const destroyGoal = () => {
    goalDestroy({
      id: goal.id
    });

    opener.close();
  };

  return (
    <Popover color={color} disabled={disabled} isOpen={opener.isOpen} opener={opener}>
      <PopoverItem
        icon={goal.completed ? 'check' : 'circle'}
        iconColor="success-500"
        onClick={updateComplete}
        textKey={goal.completed ? 'pages.carePlan.base.goals.markIncomplete' : 'pages.carePlan.base.goals.markComplete'}
      />
      <PopoverItem
        icon="boxArchive"
        onClick={updateArchived}
        textKey={goal.archived ? 'pages.carePlan.base.goals.unarchive' : 'pages.carePlan.base.goals.archive'}
      />
      <PopoverItem icon="trash" noBorder={true} onClick={destroyGoal} textKey="pages.carePlan.base.goals.delete" />
    </Popover>
  );
};

export default GoalPopover;
